import { z } from 'zod';

/** Named score for filtering in Langfuse (e.g. "user-feedback" < 1 for low-rated). */
export const USER_FEEDBACK_SCORE_NAME = 'user-feedback';

export const FEEDBACK_VALUE_POSITIVE = 1;

export const FEEDBACK_VALUE_NEGATIVE = 0;

export const FEEDBACK_COMMENT_MAX_LENGTH = 500;
export const FEEDBACK_MAX_CATEGORIES = 3;

export const NEGATIVE_FEEDBACK_CATEGORIES = [
  'incorrect-info',
  'irrelevant',
  'incomplete',
  'wrong-tool-call',
  'hallucinated-entities',
  'formatting',
  'too-slow',
  'other',
] as const;

export const POSITIVE_FEEDBACK_CATEGORIES = [
  'accurate',
  'helpful-action',
  'well-structured',
  'fast',
  'other',
] as const;

export type NegativeFeedbackCategory =
  (typeof NEGATIVE_FEEDBACK_CATEGORIES)[number];
export type PositiveFeedbackCategory =
  (typeof POSITIVE_FEEDBACK_CATEGORIES)[number];
export type FeedbackCategory =
  | NegativeFeedbackCategory
  | PositiveFeedbackCategory;

export const FEEDBACK_PROMPT_MODES = ['voluntary', 'soft'] as const;

export type FeedbackPromptMode = (typeof FEEDBACK_PROMPT_MODES)[number];

export const feedbackContextSchema = z.object({
  assistantVersion: z.string().optional(),
  sessionId: z.string().optional(),
  workspaceType: z.string().optional(),
  diagramType: z.string().optional(),
  responseLengthChars: z.number().int().nonnegative().optional(),
  latencyMs: z.number().int().nonnegative().optional(),
});

export type FeedbackContext = z.infer<typeof feedbackContextSchema>;

export const FEEDBACK_CATEGORIES = [
  ...NEGATIVE_FEEDBACK_CATEGORIES,
  ...POSITIVE_FEEDBACK_CATEGORIES,
] as const;

export const feedbackScorePayloadSchema = z.object({
  id: z.string().optional(),
  traceId: z.string().trim().min(1),
  value: z.union([
    z.literal(FEEDBACK_VALUE_POSITIVE),
    z.literal(FEEDBACK_VALUE_NEGATIVE),
  ]),
  comment: z.string().max(FEEDBACK_COMMENT_MAX_LENGTH).optional(),
  categories: z
    .array(z.enum(FEEDBACK_CATEGORIES))
    .max(FEEDBACK_MAX_CATEGORIES)
    .optional(),
  /**
   * 1-5 star rating from the soft prompt card. Optional because direct
   * thumbs-up/thumbs-down voluntary submissions don't carry a rating.
   */
  rating: z
    .union([
      z.literal(1),
      z.literal(2),
      z.literal(3),
      z.literal(4),
      z.literal(5),
    ])
    .optional(),
  prompted: z.enum(FEEDBACK_PROMPT_MODES).optional(),
  context: feedbackContextSchema.optional(),
});

export type FeedbackScorePayload = z.infer<typeof feedbackScorePayloadSchema>;
